/**
 * Shared response-handling helpers for service-layer wrappers around
 * `apiRequest()` (which returns the raw fetch Response and does not
 * parse JSON or throw on non-2xx). Service modules import from here so
 * we have a single source of truth for "parse and throw with the
 * server message".
 *
 * Introduced for GMA-1150 (caregiver access matrix UI). Other Pontosense
 * service modules (e.g. PR #155 pontosenseService.js) should import from
 * here rather than re-declaring the same parse/throw helper inline.
 */
import { wasAuthFailureReported } from "./authFailureMarker";

/**
 * Parse a fetch Response as JSON and throw if `!response.ok` or the
 * body fails to parse.
 *
 * @param {Response} response — raw response from apiRequest
 * @param {string} label      — short human-readable label for error messages
 * @returns {Promise<object>}
 */
export async function parseJsonOrThrow(response, label) {
  let body;
  try {
    body = await response.json();
  } catch (_parseError) {
    throw withResponseContext(
      new Error(`${label} returned invalid JSON (status ${response.status})`),
      response,
    );
  }
  if (!response.ok) {
    const message =
      body?.message || body?.error || `${label} failed (${response.status})`;
    throw withResponseContext(new Error(message), response);
  }
  return body;
}

/**
 * react-app #577 — attach the HTTP status as structured data on the thrown
 * error. Additive: `err.message` is unchanged, so every existing caller keeps
 * working.
 *
 * The reason this exists: `message` above is derived from the RESPONSE BODY
 * (`body.message || body.error`), so it is arbitrary backend text and may
 * carry alert content. A caller that wants to report a failure to Sentry
 * needs a field that is ours rather than the server's prose — the status code
 * is that field. Report `err.status`, never `err.message`.
 *
 * react-app #673 — also forwards baseApi's auth-failure marker. baseApi's
 * reactive 401 path reports a token failure and then falls back to RETURNING
 * the 401 rather than throwing, so the error built above was the one
 * token-failure class carrying no marker: a caller classifying its own
 * telemetry reported it a second time. Both marks come from us, never from the
 * response body, so both are safe to send to Sentry.
 */
/*
 * Exported for #731: a blob endpoint (CSV export, report download) never
 * reaches `parseJsonOrThrow`, because the success path wants `response.blob()`
 * rather than JSON. Its FAILURE path needs exactly the two fields below all
 * the same — `capturePanelFailure` de-duplicates on `status` and
 * `isAuthFailure`, so an error thrown without them makes the helper emit the
 * second event this marker exists to prevent.
 */
export function withResponseContext(error, response) {
  error.status = response.status;
  if (wasAuthFailureReported(response)) {
    error.isAuthFailure = true;
  }
  return error;
}
